import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatUnit } from '@/common/utils/formatters'
import { MaintenanceDirectiveObjectiveData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { WrenchIcon } from 'lucide-react'
import { useMemo } from 'react'

interface DiagnosticEntryObjectiveActivitiesProps {
  objective: MaintenanceDirectiveObjectiveData
  dataUpdatedAt?: number
}

const DiagnosticEntryObjectiveActivities = ({ objective, dataUpdatedAt }: DiagnosticEntryObjectiveActivitiesProps) => {
  const activitiesCountDisplay = useMemo(() => {
    const count = objective.diagnostic_activities?.length || 0

    if (count === 0) {
      return 'Nenhum serviço'
    }

    if (count === 1) {
      return '1 Serviço'
    }

    return `${count} Serviços`
  }, [dataUpdatedAt])

  const totalValueSum = useMemo(() => {
    return (
      objective.diagnostic_activities?.reduce((prev, curr) => {
        return prev + Number(curr.total_value)
      }, 0) || 0
    )
  }, [dataUpdatedAt])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <p className="font-bold text-base">Serviços</p>

          <div className="flex items-center gap-0.5 text-xs">
            <span>{activitiesCountDisplay}</span>

            <span>•</span>

            <span className="text-valid">{formatCurrency(totalValueSum)}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {objective.diagnostic_activities?.map((diagnosticActivity) => (
          <Box key={diagnosticActivity.id} className="p-4">
            <div className="flex gap-3">
              <div className="flex flex-col gap-2">
                <div className="flex gap-4">
                  <ImageCarousel
                    files={
                      diagnosticActivity.images?.map((image) => ({
                        id: image.id,
                        file: image?.url,
                      })) || []
                    }
                    Placeholder={
                      <ImageWithPlaceholder
                        className="size-28 rounded-md"
                        iconClassName="size-10"
                        placeholderIcon={WrenchIcon}
                      />
                    }
                    className="size-28"
                    showExpandButton
                    showRemoveButton={false}
                  />

                  <div className="flex flex-col gap-2">
                    <p className="text-base font-bold">{diagnosticActivity?.display_name || '--'}</p>

                    <div className="flex gap-3">
                      <ResumeItem
                        className="text-sm"
                        label="Valor unt."
                        value={formatCurrency(Number(diagnosticActivity?.unit_price) || 0)}
                      />

                      <ResumeItem
                        className="text-sm"
                        label="Valor total"
                        value={formatCurrency(Number(diagnosticActivity?.total_value) || 0)}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  {diagnosticActivity.unit_for_quantity === 'minutes' ? (
                    <ResumeItem
                      className="text-sm"
                      label="Tempo de execução em horas"
                      value={formatUnit(diagnosticActivity.quantity, 'hours')}
                    />
                  ) : (
                    <ResumeItem
                      className="text-sm"
                      label="Unidades"
                      value={formatUnit(diagnosticActivity.quantity, 'unit')}
                    />
                  )}

                  <ResumeItem
                    className="text-sm"
                    label="Profissional"
                    value={
                      diagnosticActivity.technical_professionals?.map(({ display_name }) => display_name)?.join(', ') ||
                      '--'
                    }
                  />

                  <ResumeItem
                    className="text-sm"
                    label="Garantia"
                    value={`${diagnosticActivity.warranty_in_days} Dias`}
                  />
                </div>

                <ResumeItem className="text-sm" label="Descrição" value={diagnosticActivity.description} />
              </div>
            </div>
          </Box>
        ))}
      </div>
    </div>
  )
}

export default DiagnosticEntryObjectiveActivities
